<script lang="ts">
  import { browser } from "$app/environment";
  import { asset } from "$app/paths";
  import { page } from "$app/stores";
  import LogInForm from "$components/LogInForm.svelte";
  import ProductLanding from "$components/ProductLanding.svelte";
  import { showAlert } from "$lib/alerts";
  import { isIntent } from "$lib/intents";
  import { m } from "$lib/paraglide/messages";
  import { getPriceKey, getProductByName, type Currency, type Period } from "$lib/products";
  import DOMPurify from "isomorphic-dompurify";
  import { auth } from "$lib/subscription-service";
  import { getMatomoParams } from "$lib/utils";
  import confetti from "canvas-confetti";
  import { onMount } from "svelte";
  import { fade } from "svelte/transition";
  import type { PageData } from "./$types";
  import { getLocale } from "$lib/paraglide/runtime";

  export let data: PageData;
  $: ({ intent, productName, uxMode, previousAction, email } = data);
  $: product = getProductByName(productName);

  // Note that while not currently in use, this is being left for the time when we support
  // displaying prices in different fiat currencies
  let selectedCurrency: Currency = "USD";
  let emailSubmitted = false;
  let selectedPeriod: Period;
  let confettiCanvas: HTMLCanvasElement;
  let congratsConfetti = () => {};

  // This gets triggered after the user has successfully changed their email address
  $: changeEmailAlert =
    previousAction === "change-email" &&
    showAlert({
      content: m.email_changed_success_alert(),
      type: "success",
    });

  // This will be used to pre-populate the log in form in flow for changing an email address
  $: newEmail = previousAction === "change-email" && email ? email : "";

  // In reality, this is currently the productId
  $: priceId =
    selectedPeriod && selectedCurrency && product?.prices?.get(getPriceKey(selectedCurrency, selectedPeriod))?.id;

  if (browser) {
    /**
     * Trigger login email (and confetti)
     */
    congratsConfetti = confetti.create(confettiCanvas, {
      resize: true,
    });

    // set options
    congratsConfetti = congratsConfetti.bind(null, {
      particleCount: 150,
      spread: 180,
      origin: {
        y: 1,
      },
      disableForReducedMotion: true,
    });
  }

  const handleSubmit = async (email: string) => {
    const locale = getLocale();

    const matomo_params = getMatomoParams();

    if (intent && (priceId || productName)) {
      await auth({ email, locale, ux: uxMode, intent, product_id: priceId, product_name: productName, matomo_params });
    } else {
      await auth({ email, locale, ux: uxMode, matomo_params });
    }

    emailSubmitted = true;
    changeEmailAlert?.dismiss?.();
    congratsConfetti();
  };

  // This is an unfortunate necessity for Paraglide since it doesn't support interpolating components into the strings.
  let emailNotReceivedMessage: HTMLElement;
  $: emailNotReceivedMessage?.querySelector("button#try-again")?.addEventListener("click", () => {
    const eventCategory =
      [$page.url.pathname, $page.url.searchParams.get("intent")].filter((x) => x).join("?") + " CTAs";
    const eventAction = [product?.baseNameEn && `[${product.baseNameEn}]`, "Email try again link click"]
      .filter((x) => x)
      .join(" ");
    const eventName = [product?.baseNameEn && `[${product.baseNameEn}]`, "Email try again link"].filter((x) => x).join(" ");

    // @ts-ignore
    window?._paq?.push(["trackEvent", eventCategory, eventAction, eventName]);
    emailSubmitted = false;
  });

  let show = false;
  onMount(() => {
    show = true;
  });
</script>

<div
  class="box overflow-hidden rounded-none p-0 max-md:w-full md:mb-[10vh] md:mt-[20vh] md:rounded-xl [body.product-landing_&]:mt-0 md:[body.product-landing_&]:mt-[10vh]"
>
  <!-- If we prerender this section, we'll get a flash of the wrong form in cases like ?intent=checkout -->
  {#if show}
    <div in:fade={{ duration: 200 }}>
      <header
        class="box relative z-20 flex h-[56px] items-center justify-center rounded-none border-divider-subtle bg-container-background shadow-03 max-md:p-0 md:h-auto md:shadow-none md:[body.product-landing_&]:border-b max-md:[body:not(.product-landing)_&]:mb-[10vh]"
      >
        <img class="h-[28px] md:h-[40px]" src="{asset('/images/brave-logo.svg')}" alt={m.brave_premium_logo_alt()} />
      </header>
      {#if emailSubmitted}
        <main in:fade={{ duration: 600 }} class="box max-w-[500px] rounded-none pt-0 shadow-none">
          <h1 class="text-h3 text-center text-text-secondary">{m.login_email_on_way_heading()}</h1>
          <div>
            <p class="pb-xl text-center">{m.login_email_instructions()}</p>
            <p class="text-center" bind:this={emailNotReceivedMessage}>
              {@html DOMPurify.sanitize(m.email_not_received(), { ALLOWED_TAGS: ['button'], RETURN_TRUSTED_TYPE: true })}
            </p>
          </div>
        </main>
      {:else if isIntent(intent, ["connect-receipt", "link-order"]) && product}
        <main in:fade={{ duration: 600 }} class="box max-w-[500px] rounded-none pt-0 shadow-none">
          <h1 class="text-h3 text-center text-text-secondary">{m.cold_fancy_turtle_spin({ product: product.name })}</h1>
          <p class="text-center">
            {@html DOMPurify.sanitize(m.device_usage_instructions({ product: product.name, buttonText: m.get_login_link_button() }), { ALLOWED_TAGS: ['span', 'em'], RETURN_TRUSTED_TYPE: true })}
          </p>
          <LogInForm
            onSubmit={handleSubmit}
            isVertical={true}
            eventAction="Get login link button click"
            eventName="Get login link button"
          />
        </main>
      {:else if intent === "recover" && product}
        <main in:fade={{ duration: 600 }} class="box max-w-[500px] rounded-none pt-0 shadow-none">
          <h1 class="text-h3 text-center text-text-secondary">
            {m.start_product_device_heading({ displayName: product.displayName })}
          </h1>
          <p class="text-center">
            {m.warm_pale_gecko_fetch({ displayName: product.displayName, product: product.name })}
          </p>
          <LogInForm
            onSubmit={handleSubmit}
            isVertical={true}
            eventAction={`[${product.baseNameEn}] Get login link button click`}
            eventName={`[${product.baseNameEn}] Get login link button`}
          />
        </main>
      {:else if intent === "checkout" && product}
        <ProductLanding {product} bind:selectedPeriod bind:selectedCurrency>
          {@const btnText = product.trialDays ? m.start_free_trial_button() : (selectedPeriod === "one-off" ? m.buy_now_button() : m.go_premium_button())}
          <LogInForm
            onSubmit={handleSubmit}
            isDisabled={!product.browserSupport.isSupported}
            buttonText={btnText}
            eventAction={`[${product.baseNameEn}] ${product.trialDays ? "Start free trial" : "Go premium"} button click`}
            eventName={`[${product.baseNameEn}] ${product.trialDays ? "Start free trial" : "Go premium"} button`}
          />
          <p class="text-small-regular text-center text-text-tertiary text-pretty pt-2xl">
            {@html DOMPurify.sanitize(m.step_away_product_message({ displayName: product.displayName, buttonText: btnText }), { ALLOWED_TAGS: ["span"], RETURN_TRUSTED_TYPE: true })}
          </p>
          {#if product.trialDays}
            <p class="text-small-regular text-center text-text-tertiary text-pretty">
              {m.silent_silver_elephant_zoom({ displayName: product.displayName })}
            </p>
          {/if}
        </ProductLanding>
      {:else}
        <main in:fade={{ duration: 600 }} class="box max-w-[500px] rounded-none pt-0 shadow-none">
          <h1 class="text-h3 text-center text-text-secondary">{m.login_create_account_heading()}</h1>
          <p class="text-center">
            {m.secure_login_instructions()}
          </p>
          <LogInForm
            onSubmit={handleSubmit}
            isVertical={true}
            email={newEmail}
            eventAction="Get login link button click"
            eventName="Get login link button"
          />
        </main>
      {/if}
    </div>
  {/if}
</div>

<canvas class="contents" bind:this={confettiCanvas} />

<style>
  main {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: theme("spacing.3xl");
    color: theme("colors.text.tertiary");
    margin: 0 auto;
  }
</style>
